/* Copyright (c) 2025-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { FocusMonitor } from '@angular/cdk/a11y';
import { OverlayContainer } from '@angular/cdk/overlay';
import { Inject, Injectable, NgZone, Optional } from '@angular/core';
import { Subscription } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';
import { AccessibilityService } from 'crimson/lib/accessibility/accessibility.service';
import { NV_HIG_CONFIG, NvHigConfig } from '../hig.config';
import { FocusManager } from './focus-manager';
import { SpatialNavigationService } from './spatial-navigation/spatial-navigation.service';

/**
 * NvFocusTrap creates a focus trap on a html element. When the focus is about to move to the outside of the element's subtree
 * NvFocusTrap takes the focus back to the last focused HTML element inside the container.
 *
 * @usage Attach/Detach a focus trap to/from container element
 * ```
 * const containerElement: HTMLElement = document.createElement("DIV");
 * const focusTrapFactory = Inject(NvFocusTrapFactory);
 * const focusTrap = focusTrapFactory.create(containerElement);
 * // invoke "attach" to install the listener and register it to our global focus trap stack
 * focusTrap.attach();
 * // invoke "detach" to remove the listener and deregister it from our global focus trap stack
 * focusTrap.detach();
 * ```
 */
export interface NvFocusTrap {
    /**
     * Whether the focus trap is attached to DOM
     */
    readonly isAttached: boolean;
    /**
     * Attach the focus trap to the container
     */
    attach: () => void;
    /**
     * Detach the focus trap from the container
     */
    detach: () => void;
}

@Injectable({
    providedIn: 'root'
})
export class NvFocusTrapFactory {
    constructor(
        private focusManager: FocusManager,
        private focusMonitor: FocusMonitor,
        private ngZone: NgZone,
        private spatialNavigationService: SpatialNavigationService,
        private nvFocusTrapManager: NvFocusTrapManager,
        private accessibilityService: AccessibilityService,
        private overlayContainer: OverlayContainer,
        @Optional() @Inject(NV_HIG_CONFIG) private config: NvHigConfig
    ) {}

    /**
     * Create a focus trap that traps the focus inside the given container element
     *
     * @param container
     * @returns {NvFocusTrap}
     */
    create(container: HTMLElement): NvFocusTrap {
        return new NvFocusTrapImpl(
            container,
            this.nvFocusTrapManager,
            this.focusManager,
            this.focusMonitor,
            this.ngZone,
            this.spatialNavigationService,
            this.accessibilityService,
            this.overlayContainer,
            this.config
        );
    }
}

@Injectable({
    providedIn: 'root'
})
class NvFocusTrapManager {
    private focusTraps: NvFocusTrapImpl[] = [];
    private get topFocusTrap() {
        const focusTraps = this.focusTraps;
        const length = focusTraps.length;
        return length > 0 ? focusTraps[length - 1] : undefined;
    }

    /**
     * Register the focusTrap to the NvFocusTrapManager and enable the focus trap
     *
     * The function first disables the top most focus trap in the focusTrap stack,
     * enable the passed in focus trap and push it onto the focusTrap stack.
     *
     * @param focusTrap
     */
    register(focusTrap: NvFocusTrapImpl) {
        if (!focusTrap) {
            return;
        }

        // avoid registering duplicate focus trap
        const focusTrapIndex = this.focusTraps.indexOf(focusTrap);
        if (focusTrapIndex >= 0) {
            this.focusTraps.splice(focusTrapIndex, 1);
        }

        this.topFocusTrap?.disable();
        focusTrap.enable();
        this.focusTraps.push(focusTrap);
    }

    /**
     * Remove the focusTrap from the NvFocusTrapManager and disable the focus trap
     *
     * The function removes the given focus trap in the focusTrap stack, and disable
     * it if it is attached. Then it enables the the top most focus trap in the
     * focusTrap stack.
     *
     * @param focusTrap
     */
    remove(focusTrap: NvFocusTrapImpl) {
        if (!focusTrap) {
            return;
        }

        const index = this.focusTraps.indexOf(focusTrap);
        if (index === -1) {
            return;
        }

        this.focusTraps.splice(index, 1);
        if (focusTrap.isAttached) {
            focusTrap.disable();
        }
        this.topFocusTrap?.enable();
    }
}

class NvFocusTrapImpl implements NvFocusTrap {
    private _isAttached: boolean = false;
    get isAttached() {
        return this._isAttached;
    }
    private containerOldTabIndexVal: number;
    private containerMutated: boolean = false;
    private monitorSubscription?: Subscription;

    constructor(
        private container: HTMLElement,
        private nvFocusTrapManager: NvFocusTrapManager,
        private focusManager: FocusManager,
        private focusMonitor: FocusMonitor,
        private ngZone: NgZone,
        private spatialNavigationService: SpatialNavigationService,
        private accessibilityService: AccessibilityService,
        private overlayContainer: OverlayContainer,
        private config: NvHigConfig | null
    ) {}

    attach() {
        this.nvFocusTrapManager.register(this);
    }

    detach() {
        this.nvFocusTrapManager.remove(this);
    }

    /**
     * Start monitoring the focus on the container, and move the focus back to the
     * last focused element in the contaienr's subtree when the focus is about to
     * be moved to an element outside of the container's subtree
     *
     * @returns {boolean} true if the focus trap is attached successfully, otherwise false.
     */
    enable(): boolean {
        if (this._isAttached) {
            return true;
        }

        if (!this.spatialNavigationService.isFocusable(this.container)) {
            this.containerOldTabIndexVal = this.container.tabIndex;
            this.container.tabIndex = 0;
            this.containerMutated = true;
        }

        if (!this.container.contains(document.activeElement)) {
            this.focusManager.focusViaLastOrigin(this.container);
        }

        this.monitorFocusOn(this.container, document.activeElement as HTMLElement);

        this._isAttached = true;
        return this._isAttached;
    }

    /**
     * Stop monitoring the focus on the container
     */
    disable() {
        if (!this._isAttached) {
            throw new Error('invoked detach when the focus trap is not attached');
        }

        if (this.containerMutated) {
            this.container.tabIndex = this.containerOldTabIndexVal;
            this.containerMutated = undefined;
            this.containerOldTabIndexVal = undefined;
        }

        this.monitorSubscription?.unsubscribe();
        this.monitorSubscription = undefined;
        this._isAttached = false;
    }

    private monitorFocusOn(focusTrap: HTMLElement, initialFocus: HTMLElement) {
        let lastFocusedElement: HTMLElement = initialFocus;
        this.ngZone.runOutsideAngular(() => {
            this.monitorSubscription = this.focusMonitor
                .monitor(focusTrap, true)
                .pipe(withLatestFrom(this.accessibilityService.onScreenReaderStateChanged()))
                .subscribe(([origin, isScreenReaderEnabled]) => {
                    if (!origin) {
                        // element is about to be removed from DOM tree
                        // put the check in queue until DOM is updated
                        setTimeout(() => {
                            // this timeout callback may be invoked after the focus trap is detached
                            // for such case, we don't want to invoke this callback
                            if (!this._isAttached) {
                                return;
                            }

                            // Focus moved into a CDK overlay panel (e.g. mat-select dropdown, autocomplete).
                            // These components return focus to their trigger when the panel closes — do not interfere.
                            if (this.overlayContainer.getContainerElement().contains(document.activeElement)) {
                                return;
                            }

                            const element =
                                this.focusManager.currentFocusedElement !== document.body ||
                                lastFocusedElement?.isConnected
                                    ? lastFocusedElement
                                    : focusTrap;

                            const isUnfocused = !this.focusManager.currentFocusedElement.matches(':focus');
                            const screenReaderFocusClearWorkaroundNeeded =
                                !!this.config?.screenReaderFocusClearWorkaround && isScreenReaderEnabled && isUnfocused;

                            if (!screenReaderFocusClearWorkaroundNeeded) {
                                this.focusManager.focusViaLastOrigin(element);
                            }
                        });
                    } else {
                        lastFocusedElement = this.focusManager.currentFocusedElement;
                    }
                });
        });
    }
}
